import { FC } from 'react'
import { AttorneyData } from "src/dtos/applicationData"
import { 
  FlexDiv, 
  ClaimItemFormMainContainer,
  ClaimItemFormSection,
  ClaimItemTitle, 
  FormInputTitle,
} from './styles'
import { LeftBar } from '../../basic/LeftBar'
import StandardInput from '../../basic/StandardInput'
import StepBullet from '../../basic/StepBullet'
import StandardCheckbox from '../../basic/StandardCheckbox'
import StandardFormatInput from '../../basic/StandardFormatInput'
import OptionButton from '../../basic/OptionButton'
import { attorneyFullName } from 'src/utils/newApplications'
import { validateDateField } from 'src/utils/formDataManipulation'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

interface ICOCItemFormProps {
  attorney: AttorneyData;
  attorneyIndex: number;
  setAttorney: (data: Partial<AttorneyData>) => void;
  isIncorrect?: boolean;
  errorCodes: string[];
  validationErrors?: any
}

const ICOCItemForm: FC<ICOCItemFormProps> = ({ 
  attorney, 
  attorneyIndex, 
  setAttorney,
  isIncorrect,
  errorCodes = [],
  validationErrors,
}) => {
  const handleSelectCheckbox = (option: string) => {
    if(!attorney?.tasksPerformed) {
      setAttorney({ tasksPerformed: [option] })
    } else {
      const hasOptionAlready = attorney.tasksPerformed.includes(option)
      if(hasOptionAlready) {
        setAttorney({ tasksPerformed: attorney.tasksPerformed.filter(task => task !== option) })
      } else {
        setAttorney({ tasksPerformed: [ ...attorney.tasksPerformed, option ] })
      }
    }
  }

  const idPrefix = [attorney?.firstName, attorney?.lastName].join('').replaceAll(' ', '_')
  
  return (
    <ClaimItemFormMainContainer>
      <ClaimItemTitle>
        <h1>Attorney {attorneyIndex + 1}</h1>
        <hr />
      </ClaimItemTitle>
      <FlexDiv gap="18px" margin="48px 0 0 0">
        <LeftBar />
        <FlexDiv gap="80px" dir='column'>

          <ClaimItemFormSection maxWidth='80%'>
            <StepBullet id={`${idPrefix}__attorneyName`}>1</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Attorney</FormInputTitle>
              <FlexDiv maxWidth='440px'>
                <StandardInput
                  label=""
                  value={attorneyFullName(attorney)}
                  disabled
                />
              </FlexDiv>
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection maxWidth='80%'>
            <StepBullet id={`${idPrefix}__yearStartedAsICOC`}>2</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Year attorney became Independent Contractor or Of Counsel</FormInputTitle>
              <FlexDiv maxWidth='440px'>
                <StandardInput
                  label=""
                  type="number"
                  placeholder='YYYY'
                  value={attorney?.yearStartedAsICOC ?? ''}
                  onChange={({target: { value }}) => setAttorney({ yearStartedAsICOC: !!value ? Number(value) : undefined })}
                  isIncorrect={isIncorrect && (validationErrors.includes(`yearStartedAsICOC:${attorneyIndex}`) || validationErrors.includes('yearStartedAsICOC must be an integer'))}
                  errorMessage={isIncorrect && 
                              (validationErrors.includes(`yearStartedAsICOC:${attorneyIndex}`) || validationErrors.includes('yearStartedAsICOsC must be an integer')) 
                              ? 'Format is invalid.  Must be YYYY' : ''}
                  errorCode={errorCodes[2]}
                />
              </FlexDiv>
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection maxWidth='80%'>
            <StepBullet id={`${idPrefix}__hoursOnBill`}>3</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>In the past 12 months, how many hours did the Attorney bill on behalf of your firm?</FormInputTitle>
              <FlexDiv maxWidth='180px'>
                <StandardInput 
                  label=""
                  type="number"
                  value={attorney?.hoursOnBill ?? ''}
                  onChange={({target: { value }}) => setAttorney({ hoursOnBill: (!!value && !!Number(value)) ? Number(value) : undefined })}
                  isIncorrect={isIncorrect && validationErrors.includes(`hoursOnBill:${attorneyIndex}`)}
                  errorMessage={isIncorrect && validationErrors.includes(`hoursOnBill:${attorneyIndex}`) ? 'Format is invalid, must be a whole number' : ''}
                  errorCode={errorCodes[3]}
                />
              </FlexDiv>
            </FlexDiv>
          </ClaimItemFormSection>
          
          <ClaimItemFormSection maxWidth='80%'>
            <StepBullet id={`${idPrefix}__hoursBilledInFirstYear`}>4</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>
                If the attorney has been acting as Independent Contractor or Of Counsel for your firm for less than 12 months, 
                how many hours do you estimate the attorney
                will bill on behalf of your firm during their first year?
              </FormInputTitle>
              <FlexDiv maxWidth='180px'>
                <StandardInput 
                  label=""
                  type="number"
                  value={attorney?.hoursBilledInFirstYear ?? ''}
                  onChange={({target: { value }}) => setAttorney({ hoursBilledInFirstYear: !!value ? Number(value) : undefined })}
                  isIncorrect={isIncorrect && validationErrors.includes(`hoursBilledInFirstYear:${attorneyIndex}`)}
                  errorMessage={isIncorrect && validationErrors.includes(`hoursBilledInFirstYear:${attorneyIndex}`) ? 'Format is invalid, must be a whole number' : ''}
                  errorCode={errorCodes[4]}
                />
              </FlexDiv>
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection maxWidth='80%'>
            <StepBullet>5</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Does the attorney also practice independently from your firm?</FormInputTitle>
              <FlexDiv gap="16px">
                <OptionButton
                  checked={attorney?.actsIndependentlyFromFirm === true}
                  onChange={() => setAttorney({ actsIndependentlyFromFirm: true })}
                  errorCode={errorCodes[5]}
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={attorney?.actsIndependentlyFromFirm === false}
                  onChange={() => setAttorney({
                    actsIndependentlyFromFirm: false,
                    actsIndependentlyFromFirmWhere: undefined
                  })}
                  errorCode={errorCodes[5]}
                >
                  No
                </OptionButton>
              </FlexDiv>
              <ValidationErrorMessage errorCode={errorCodes[5]} />
              {attorney?.actsIndependentlyFromFirm === true ? (
                <FlexDiv gap='24px'>
                  <LeftBar />
                  <FlexDiv dir='column' gap="12px">
                    <FormInputTitle>Where does the attorney practice independently from your firm?</FormInputTitle>
                    <FlexDiv maxWidth='440px'>
                      <StandardInput
                        label=""
                        value={attorney?.actsIndependentlyFromFirmWhere ?? ''}
                        onChange={e => setAttorney({ actsIndependentlyFromFirmWhere: e.target.value })}
                        errorCode={errorCodes[6]}
                      />
                    </FlexDiv>
                  </FlexDiv>
                </FlexDiv>
              ) : null}
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection maxWidth='80%'>
            <StepBullet>6</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Does the Attorney have professional liability insurance for that independent practice?</FormInputTitle>
              <FlexDiv gap="16px">
                <OptionButton
                  checked={attorney?.hasInsuranceForIndependentPractice === true}
                  onChange={() => setAttorney({ hasInsuranceForIndependentPractice: true })}
                  errorCode={errorCodes[7]}
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={attorney?.hasInsuranceForIndependentPractice === false}
                  onChange={() => setAttorney({
                    hasInsuranceForIndependentPractice: false,
                    insuranceCompanyNameOfIndependent: undefined,
                    effectivePolicyDateFrom: undefined,
                    effectivePolicyDateTo: undefined,
                    liabilityLimitsOfPolicy: undefined
                  })}
                  errorCode={errorCodes[7]}
                >
                  No
                </OptionButton>
              </FlexDiv>
              <ValidationErrorMessage errorCode={errorCodes[7]} />
              {attorney?.hasInsuranceForIndependentPractice === true ? (
                <FlexDiv gap="24px">
                  <LeftBar />
                  <FlexDiv dir="column" gap="24px">
                    <FlexDiv dir='column' gap="8px">
                      <FormInputTitle>What is the name of the insurance company?</FormInputTitle>
                      <FlexDiv maxWidth='440px'>
                        <StandardInput 
                          label=""
                          value={attorney?.insuranceCompanyNameOfIndependent ?? ''}
                          onChange={e => setAttorney({ insuranceCompanyNameOfIndependent: e.target.value })}
                        />
                      </FlexDiv>
                    </FlexDiv>
                    <FlexDiv dir='column' gap="8px">
                      <FormInputTitle>What are the effective dates of the policy?</FormInputTitle>
                      <FlexDiv maxWidth='400px' gap="40px">
                        <StandardFormatInput
                          label='From'
                          maskStyle='date'
                          leftIcon='calendar'
                          value={attorney?.effectivePolicyDateFrom ?? ''}
                          onValueChange={({ formattedValue }) => setAttorney({ effectivePolicyDateFrom: formattedValue })}
                          isIncorrect={!!attorney?.effectivePolicyDateFrom && !!validateDateField(attorney?.effectivePolicyDateFrom, true)}
                          errorMessage={!!attorney?.effectivePolicyDateFrom && !!validateDateField(attorney?.effectivePolicyDateFrom) ? validateDateField(attorney?.effectivePolicyDateFrom, true) : ''}
                          errorCode={errorCodes[8]}
                        />
                        <StandardFormatInput
                          label='To'
                          maskStyle='date'
                          leftIcon='calendar'
                          value={attorney?.effectivePolicyDateTo ?? ''}
                          onValueChange={({ formattedValue }) => setAttorney({ effectivePolicyDateTo: formattedValue })}
                          isIncorrect={!!attorney?.effectivePolicyDateTo && !!validateDateField(attorney?.effectivePolicyDateTo, true)}
                          errorMessage={!!attorney?.effectivePolicyDateTo && !!validateDateField(attorney?.effectivePolicyDateTo, true) ? validateDateField(attorney?.effectivePolicyDateTo, true) : ''}
                          errorCode={errorCodes[9]}
                        />
                      </FlexDiv>
                    </FlexDiv>
                    <FlexDiv dir='column' gap="8px">
                      <FormInputTitle>What are the limits of liability of the policy?</FormInputTitle>
                      <FlexDiv maxWidth='200px'>
                        <StandardInput
                          label=""
                          value={attorney?.liabilityLimitsOfPolicy ?? ''}
                          onChange={(e) => setAttorney({ liabilityLimitsOfPolicy: e.target.value })}
                        />
                      </FlexDiv>
                    </FlexDiv>
                  </FlexDiv>
                </FlexDiv>
              ) : null}
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection maxWidth='80%'>
            <StepBullet>7</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Which of the following tasks does the attorney perform on behalf of your firm? (select all that apply)</FormInputTitle>
              <FlexDiv dir="column" gap="24px">
                <StandardCheckbox
                  label="Appear in court"
                  variation='thin'
                  checked={attorney?.tasksPerformed && attorney?.tasksPerformed.includes("Appear in court")}
                  onChange={() => handleSelectCheckbox("Appear in court")}
                />
                <StandardCheckbox
                  label="Sign pleadings"
                  variation='thin'
                  checked={attorney?.tasksPerformed && attorney?.tasksPerformed.includes("Sign pleadings")}
                  onChange={() => handleSelectCheckbox("Sign pleadings")}
                />
                <StandardCheckbox
                  label="Have direct contact with your firm’s clients"
                  variation='thin'
                  checked={attorney?.tasksPerformed && attorney?.tasksPerformed.includes("Have direct contact with your firm’s clients")}
                  onChange={() => handleSelectCheckbox("Have direct contact with your firm’s clients")}
                />
              </FlexDiv>
            </FlexDiv>
          </ClaimItemFormSection>
        </FlexDiv>
      </FlexDiv>
    </ClaimItemFormMainContainer>
  )
}

export default ICOCItemForm